<template>
	<div class="container">
		<KnockoutsTable
			:loanId="loanId"
			:knockouts="knockouts"
			:isLoading="isLoading"
			:borrowerCreditPullTypeId="borrowerCreditPullTypeId"
			:coBorrowerCreditPullTypeId="coBorrowerCreditPullTypeId" />

		<AlertsTable
			:loanId="loanId"
			:alerts="alerts"
			:isLoading="isLoading"
			:borrowerCreditPullTypeId="borrowerCreditPullTypeId"
			:coBorrowerCreditPullTypeId="coBorrowerCreditPullTypeId" />

		<a v-if="hasCreditDetails" @click="openReportModal"> View Credit Report(s) </a>

		<CreditReportViewerModal
			v-if="viewingReport"
			:annualReviewId="annualReviewId"
			:creditPullTypeId="creditPullTypeId"
			@close="closeReportModal" />
	</div>
</template>

<script setup lang="ts">
	import { computed, onMounted, ref } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import { AnnualReviewCreditDetails, KnockoutModel, AlertModel } from '@/models/loans';
	import KnockoutsTable from '@/components/loan/annual-review/KnockoutsTable.vue';
	import AlertsTable from '@/components/loan/annual-review/AlertsTable.vue';
	import CreditReportViewerModal from '@/components/loan/annual-review/CreditReportViewerModal.vue';

	const props = defineProps<{
		loanId: number;
		annualReviewId: number | undefined;
		lenderId: number;
		productTypeId: number;
		programTypeId: number;
		creditPullTypeId: number;
    	applicationTypeId: number;
	}>();

	const isLoading = ref<boolean>(false);
	const viewingReport = ref(false);
	const knockouts = ref<KnockoutModel[]>([]);
	const alerts = ref<AlertModel[]>([]);
	const borrowerCreditPullTypeId = ref<number>(1);
	const coBorrowerCreditPullTypeId = ref<number>(1);
	const hasCreditDetails = computed(() => knockouts.value.length || alerts.value.length);

	const closeReportModal = () => (viewingReport.value = false);
	const openReportModal = () => {
		if (isLoading.value == true) {
			return;
		}

		viewingReport.value = true;
	};

	const fetchCreditDetailsAsync = async () => {
		isLoading.value = true;

		try {
			const response = await axios.post<AnnualReviewCreditDetails>(
				`/api/annualreview/${props.annualReviewId}/credit-details`,
				{
					lenderId: props.lenderId,
					programTypeId: props.programTypeId,
					productTypeId: props.productTypeId,
					creditPullTypeId: props.creditPullTypeId,
          			applicationTypeId: props.applicationTypeId
				}
			);

			knockouts.value = response.data.knockouts;
			alerts.value = response.data.alerts;
			borrowerCreditPullTypeId.value = response.data.borrowerCreditPullTypeId;
			coBorrowerCreditPullTypeId.value = response.data.coBorrowerCreditPullTypeId;
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue getting the credit report details. Please try again later.'
			});
		} finally {
			isLoading.value = false;
		}
	};

	onMounted(async () => {
		await fetchCreditDetailsAsync();
	});
</script>

<style scoped>
	.container {
		display: flex;
		flex-direction: column;
	}

	a {
		position: relative;
		align-self: flex-end;
		text-decoration: none;
		cursor: pointer;
	}

	a::after {
		content: '';
		position: absolute;
		left: 0;
		bottom: -2px;
		width: 0;
		height: 2px;
		background-color: black;
		transition: width 0.1s ease;
	}

	a:hover::after {
		width: 100%;
	}
</style>